import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { Spin, Switch, Tooltip } from 'antd';
import { ScopeTabs } from 'components/Custom/tabs';
import { ReactComponent as GoldCheck } from 'assets/images/icons/goldCheck.svg';
import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg';
import { ReactComponent as IconFuzzyMapping } from 'assets/images/icons/iconFuzzyMapping.svg';
import { ReactComponent as IconQuestionMark } from 'assets/images/icons/iconQuestionMark.svg';
import { ReactComponent as UploadCheck } from 'assets/images/icons/uploadCheck.svg';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { FormSelect } from 'components/Custom/selects';
import { ColoredTag } from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import dayjs from 'dayjs';
import { excelColumnNames } from 'pages/organisations/organisation';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY } from 'shared/constants/transactionsTourKeys';
import { turnModalOff } from 'store/app/actions';
import { getExcelTransactionAccountTypeLabel } from 'utils/display-enum-label';
import { getAuthUser } from 'store/auth/selectors';
import {
  clearUploadExcelResult,
  clearUploadTransactionsTemp,
  fetchExcelFilesMetadataRequest,
  fetchUploadedFileTransactionsRequest,
  uploadExcelTransactionsRequest,
} from 'store/organisation/actions';
import type { ValidatedExcelTransaction } from 'store/organisation/models';
import {
  formatExcelTransactionDateForDisplay,
} from 'store/organisation/reducer/excelHandlers';
import {
  getChartOfAccountsForOrganisation,
  getOrgLoading,
  getUploadExcelTransactionsStatus,
  getUploadExcelTransactionsValidationData,
  getUploadTransactionsTemp,
  getUploadedFileTransactionsPage,
} from 'store/organisation/selectors';
import styled, { createGlobalStyle } from 'styled-components';
import {
  tryGetMapImportFieldLabel,
} from 'shared/utils/map-import-field-label';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import type * as XLSXTypes from 'xlsx';

/**
 * Order for auto-map and fuzzy matching. Fields are processed in this order when
 * claiming columns, which affects which field gets ambiguous matches (e.g.
 * "Invoice No" vs "Invoice Number"). Kept separate from excelColumnNames so UI
 * display order can differ without breaking matching logic.
 */
const FIELD_MATCH_ORDER: string[] = [
  'Upload Date',
  'Transaction Date',
  'Due Date',
  'Source Type',
  'Transaction Number',
  'Currency',
  'Exchange Rate',
  'Discount Amount',
  'Reference',
  'Total Amount',
  'Net Amount',
  'VAT Paid',
  'Tax Name',
  'Tax Rate',
  'Description',
  'Name',
  'VAT Number',
  'Country',
  'Account Name',
  'Account Type',
];
const EXCEL_HIDDEN_MAPPING_FIELDS = new Set<string>([
  'Upload Date',
  'Due Date',
  'Currency',
  'Exchange Rate',
  'Discount Amount',
  'Item',
]);

// Map display column names (from mapping) to validatedExcelTransaction API keys (exported for transactions tab table)
export const VALIDATION_COLUMN_KEY_MAP: Record<
  string,
  keyof ValidatedExcelTransaction
> = {
  'Transaction Date': 'transactionDate',
  'Due Date': 'dueDate',
  Currency: 'currency',
  'Exchange Rate': 'exchangeRate',
  'Discount Amount': 'discountAmount',
  'Source Type': 'sourceType',
  'Transaction Number': 'transactionNumber',
  Reference: 'reference',
  'Total Amount': 'gross',
  'Net Amount': 'net',
  'VAT Paid': 'tax',
  'Tax Name': 'taxName',
  'Tax Rate': 'taxRate',
  Description: 'description',
  'Is Customer/Supplier': 'contactType',
  Name: 'contactName',
  'VAT Number': 'vatNumber',
  Country: 'country',
  'Account Name': 'accountCode',
  Item: 'itemId',
  Quantity: 'quantity',
  'Unit Price': 'unitPrice',
  'Tax Inclusive': 'taxInclusive',
  'Account Type': 'accountType',
  'Upload Date': 'uploadDate',
  'Error Reason': 'errorReason',
};

/** Excel upload mapping UI: fields the provider does not support (matches transactions-tab itemId gating). */
function isExcelProviderMappingField(displayName: string): boolean {
  if (displayName === 'Is Customer/Supplier') return false;
  if (EXCEL_HIDDEN_MAPPING_FIELDS.has(displayName)) return false;
  if (VALIDATION_COLUMN_KEY_MAP[displayName] === 'itemId') return false;
  if (VALIDATION_COLUMN_KEY_MAP[displayName] === 'quantity') return false;
  if (VALIDATION_COLUMN_KEY_MAP[displayName] === 'unitPrice') return false;
  if (VALIDATION_COLUMN_KEY_MAP[displayName] === 'taxInclusive') return false;
  return true;
}

const ALWAYS_OPTIONAL_MAPPING_FIELDS = new Set<string>([
  'Due Date',
  'Currency',
  'Exchange Rate',
  'Discount Amount',
  'Transaction Number',
  'Reference',
  'Description',
  'VAT Number',
  'Country',
  'Upload Date',
  'Source Type'
]);

/** Account Name and Account Type are optional only when neither is mapped. */
function isOptionalMappingField(
  fieldName: string,
  mappings: Record<string, string | undefined>
): boolean {
  if (fieldName === 'Account Name' || fieldName === 'Account Type') {
    const otherField =
      fieldName === 'Account Name' ? 'Account Type' : 'Account Name';
    return !mappings[otherField];
  }
  return ALWAYS_OPTIONAL_MAPPING_FIELDS.has(fieldName);
}

function canEverBeOptionalMappingField(fieldName: string): boolean {
  return (
    fieldName === 'Account Name' ||
    fieldName === 'Account Type' ||
    ALWAYS_OPTIONAL_MAPPING_FIELDS.has(fieldName)
  );
}

/** Strip "[Row N]" prefix and return error messages split by semicolon. */
function parseErrorMessages(errorReason: string | null | undefined): string[] {
  if (errorReason == null || String(errorReason).trim() === '') return [];
  const cleaned = String(errorReason)
    .replace(/^\[\s*Row\s*\d+\s*\]\s*/i, '')
    .trim();
  if (!cleaned) return [];
  return cleaned
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
}

const LABEL_TO_TOOLTIP_KEY: Record<string, string> = {
  'Transaction Date': 'transactionDate',
  'Due Date': 'dueDate',
  Currency: 'currency',
  'Exchange Rate': 'exchangeRate',
  'Discount Amount': 'discountAmount',
  'Source Type': 'sourceType',
  'Transaction Number': 'transactionNumber',
  Reference: 'reference',
  'Total Amount': 'totalAmount',
  'Net Amount': 'netAmount',
  'VAT Paid': 'tax',
  'Tax Name': 'taxName',
  'Tax Rate': 'taxRate',
  Description: 'description',
  'Is Customer/Supplier': 'isCustomerSupplier',
  Name: 'name',
  'VAT Number': 'vatNumber',
  'Tax / VAT Number': 'vatNumber',
  Country: 'country',
  'Account Code': 'accountCode',
  'Account Name': 'accountCode',
  Item: 'itemId',
  'Account Type': 'accountType',
  'Tax Inclusive': 'taxInclusive',
  'Upload Date': 'uploadDate',
};

// Map tooltip text (when Excel header is the long description) to key so we show short label + tooltip
const TOOLTIP_TEXT_TO_KEY: Record<string, string> = {
  'The date of the transaction which will be used for the import.':
    'transactionDate',
  'The due date of the transaction (optional).': 'dueDate',
  "Currency of the transaction - this is used to convert the transaction amount to the entity's currency":
    'currency',
  "Exchange rate used to convert transaction amounts to the entity's currency (optional).":
    'exchangeRate',
  'Discount amount applied to the transaction (optional).': 'discountAmount',
  'Choose between Sale and Purchase': 'sourceType',
  'An optional field which can be used to enter a reference code/number':
    'reference',
  'Total amount including tax/VAT': 'totalAmount',
  'Amount excluding tax/vat': 'netAmount',
  'All taxes need to be given a unique name': 'taxName',
  'The % tax rate used in this transaction': 'taxRate',
  'Transaction line description': 'description',
  'Choose between Customer or Supplier': 'isCustomerSupplier',
  'Name of the Customer or Supplier': 'name',
  'Tax / VAT number of the Customer or Supplier (important for VAT Number Checker)':
    'vatNumber',
  'Country of Customer or Supplier': 'country',
  'The account name associated with this transaction': 'accountCode',
  'The item associated with this transaction line': 'itemId',
  'The type of account (e.g. Revenue, Expense, etc.)': 'accountType',
  'Date the transaction was uploaded to generate.TAX (read-only).':
    'uploadDate',
};

/** Synonyms for fuzzy matching: expected field name -> alternative names that may appear in Excel. */
const EXPECTED_FIELD_SYNONYMS: Record<string, string[]> = {
  'Transaction Date': ['Transaction Date', 'Date', 'Invoice Date', 'Bill Date'],
  'Due Date': ['Due Date', 'Payment Due Date', 'Due'],
  Currency: ['Currency', 'Curr', 'CCY', 'Currency Code'],
  'Exchange Rate': ['Exchange Rate', 'FX Rate', 'Rate'],
  'Discount Amount': ['Discount Amount', 'Discount', 'Disc Amount'],
  'Total Amount': ['Total Amount', 'Gross Amount', 'Amount'],
  'VAT Paid': ['VAT Paid', 'VAT', 'VAT Amount'],
  'Transaction Number': ['Transaction Number', 'Invoice Number', 'Bill Number'],
  Reference: [
    'Reference',
    'Ref',
    'Ref No',
    'Reference No',
    'Invoice Reference',
    'Bill Reference',
    'Invoice Ref',
    'Bill Ref',
    'Invoice No',
    'Bill No',
  ],
  Country: [
    'Country',
    'Customer Country',
    'Supplier Country',
    'Country of Supply',
  ],
  'Upload Date': ['Upload Date', 'Uploaded', 'Date Uploaded'],
  Name: ['Name', 'Client Name', 'Supplier Name', 'Customer Name'],
  'VAT Number': [
    'VAT Number',
    'Tax / VAT Number',
    'Tax Number',
    'VAT No',
    'Tax No',
  ],
  Description: [
    'Description',
    'Desc',
    'Details',
    'Notes',
    'Remarks',
    'Comment',
    'Line Description',
    'Item Description',
  ],
  'Account Name': [
    'Account Name',
    'Account Code',
    'Account No',
    'Account Number',
    'Acct Code',
    'GL Code',
  ],
  Item: ['Item', 'Product', 'Service', 'Item Name', 'Product Name'],
  'Account Type': ['Account Type', 'Acct Type', 'Account Category'],
  'Is Customer/Supplier': ['Is Customer/Supplier', 'Customer or Supplier'],
};

/** Normalised string similarity 0–1 (1 = identical). Uses Levenshtein distance. */
function stringSimilarity(a: string, b: string): number {
  const sa = a.toLowerCase().trim();
  const sb = b.toLowerCase().trim();
  if (sa === sb) return 1;
  if (sa.length === 0 || sb.length === 0) return 0;

  const lenA = sa.length;
  const lenB = sb.length;
  const maxLen = Math.max(lenA, lenB);

  const d: number[][] = Array.from({ length: lenA + 1 }, () =>
    Array(lenB + 1).fill(0)
  );
  for (let i = 0; i <= lenA; i++) d[i][0] = i;
  for (let j = 0; j <= lenB; j++) d[0][j] = j;

  for (let i = 1; i <= lenA; i++) {
    for (let j = 1; j <= lenB; j++) {
      const cost = sa[i - 1] === sb[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );
    }
  }

  const distance = d[lenA][lenB];
  return 1 - distance / maxLen;
}

/**
 * Best match from candidates; uses target and any synonyms to maximise score.
 * If containWords is set, candidates that include any of those phrases get a minimum score boost.
 * If rejectIfContains is set, candidates that include any of those phrases (and don't satisfy containWords) are skipped.
 */
function bestFuzzyMatch(
  target: string,
  candidates: string[],
  exclude: Set<string>,
  minScore: number,
  synonyms: string[] = [],
  containWords?: string | string[],
  rejectIfContains?: string[]
): { candidate: string; score: number } | undefined {
  const targets = [target, ...synonyms];
  const CONTAIN_WORD_BOOST = 0.65;
  const words = containWords
    ? Array.isArray(containWords)
      ? containWords
      : [containWords]
    : [];
  const rejectPhrases = rejectIfContains ?? [];
  let best: { candidate: string; score: number } | undefined;
  for (const c of candidates) {
    if (exclude.has(c)) continue;
    const cLower = c.toLowerCase().trim();
    const hasRequiredWord = words.some(
      (w) => w && cLower.includes(w.toLowerCase().trim())
    );
    const hasRejectPhrase = rejectPhrases.some(
      (r) => r && cLower.includes(r.toLowerCase().trim())
    );
    if (rejectPhrases.length > 0 && hasRejectPhrase && !hasRequiredWord) {
      continue; // e.g. "Invoice No" must not match Transaction Date
    }
    let score = Math.max(...targets.map((t) => stringSimilarity(t, c)));
    if (hasRequiredWord) {
      score = Math.max(score, CONTAIN_WORD_BOOST);
    }
    if (score >= minScore && (!best || score > best.score)) {
      best = { candidate: c, score };
    }
  }
  return best;
}

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  //height: 500px;
  .ant-table-title {
    padding: 0 !important;
  }
`;

const SuccessHeader = styled.div`
  background-color: var(--color-primary, #41aaaf);
  border-radius: 20px 20px 0 0;
  padding: 16px 25px;
  margin: -24px -24px 0px -24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
`;

const CheckIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SuccessMessage = styled.div`
  color: #ffffff;
  font-family: Inter;
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
`;

const FileName = styled.div`
  font-family: Inter;
  font-size: 12px;
  font-weight: 300;
  line-height: 20px;
  color: #f3f7f8;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 16px;
  flex: 1;
`;

const ProgressTracker = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressStep = styled.div<{
  $isCompleted?: boolean;
  $isActive?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-family: Inter;
  font-size: 14px;
  font-weight: 300;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0%;
`;

const StepCircle = styled.div<{ $isCompleted?: boolean; $isActive?: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.$isCompleted
      ? 'transparent'
      : props.$isActive
      ? '#FFBC3E'
      : 'var(--color-layout-bg, #E9F6F7)'};
  border: none;
  color: var(--color-text-dark, #042a2a);
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  font-style: normal;
  line-height: 18px;
  letter-spacing: 0%;
`;

const StepConnector = styled.div`
  width: 52px;
  height: 0px;
  border: 1px solid var(--color-border, #dbeae3);
`;

const ModalContent = styled.div`
  padding: 0;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 16px;
  height: 60px;
  gap: 16px;
`;

const ContentHeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const ContentTitle = styled.h3`
  margin: 0;
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  height: 45px;
  display: flex;
  align-items: center;
  color: var(--color-text-dark, #042a2a);
  border-bottom: 1px solid #f0f0f0;
`;

const ResultStepTitleBlock = styled.div`
  margin-top: -8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const ResultStepTitle = styled.div`
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: var(--color-text-dark, #042a2a);
`;

const ResultStepDescription = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #5b6b73;
  margin-top: 4px;
  line-height: 1.4;
`;

const ResultStepHelpLink = styled.a`
  color: var(--color-primary, #41aaaf);
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    color: var(--color-primary-hover, #64bbbd);
  }
`;

const UPLOAD_TRANSACTIONS_HELP_URL =
  'https://help.generate.tax/portal/en/kb/articles/import-data-using-excel-csv#Upload_your_transactions';

const ResultStepWarningBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid #ffbc3e;
  background-color: var(--color-status-warning-bg, #fff5e2);
  .anticon-exclamation-circle {
    color: #c38a20;
    font-size: 20px;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const ResultStepSuccessBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid var(--color-primary, #41aaaf);
  background-color: var(--color-layout-bg, #e9f6f7);
  .anticon-check-circle {
    color: var(--color-primary, #41aaaf);
    font-size: 20px;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const TabsContainer = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
`;

const ContentHeaderRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 0;
`;

const FuzzyMappingButton = styled(ScopeButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  width: auto;
`;

const FuzzyMappingIcon = styled(IconFuzzyMapping)`
  width: 18px;
  height: 18px;
`;

const TabsContent = styled.div`
  min-height: 0;
  overflow: visible;
`;

const AllMappedMessage = styled.div`
  font-family: Inter;
  font-size: 14px;
  color: var(--color-text-dark, #042a2a);
  padding: 16px 0;
`;

const PreviewTableWrapper = styled.div`
  max-width: 1200px;

  .preview-table .ant-table-cell,
  .validation-errors-table .ant-table-cell {
    padding: 0 12px !important;
    font-size: 12px !important;
  }

  .preview-table .ant-table-thead .ant-table-cell,
  .validation-errors-table .ant-table-thead .ant-table-cell {
    font-size: 12px !important;
  }
  .ant-table-title {
    padding: 0 !important;
  }
`;

const PreviewColumnHeaderWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PreviewColumnHeaderMapped = styled.span`
  font-size: 10px;
  color: var(--color-text-muted, #5a6a6a);
  font-weight: 400;
  line-height: 1.2;
  min-height: 12px;
`;

const PreviewTableTitle = styled(ContentTitle)`
  font-size: 13px;
`;

const FooterButtons = styled.div`
  margin-top: 16px;
  margin-bottom: -12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const FooterButton = styled(ScopeButton)`
  width: 96px;
  height: 32px;
  font-size: 13px;
  border-radius: 50px;
`;

const FooterRightButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 32px;
  row-gap: 12px;
  width: 100%;
`;

const ColumnList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
`;

const ColumnItem = styled.li`
  padding: 7px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border-radius: 10px;

  &:hover {
    background-color: var(--color-layout-bg, #e9f6f7);
  }
`;

const ColumnName = styled.span`
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0%;
  color: var(--color-text-muted, #5a6a6a);
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ColumnLabelWithTooltip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 16px;
`;

const ColumnOptionalHint = styled.span<{ $visible: boolean }>`
  display: block;
  font-size: 9px;
  color: #888;
  margin-top: 3px;
  height: 12px;
  line-height: 12px;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;

const QuestionMarkIconWrapper = styled.span`
  color: rgb(181, 181, 181) !important;
  display: inline-flex;
  cursor: pointer;
  margin-top: -2px;

  & svg,
  & path {
    fill: rgb(181, 181, 181) !important;
  }
`;

const MapFormSelect = styled(FormSelect)`
  .ant-select-selector,
  .ant-select-selection-item {
    font-size: 12px !important;
  }

  .ant-select-selector {
    min-height: 33px !important;
    height: 33px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    box-sizing: border-box;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: 31px !important;
  }
`;

const UploadColumnsDropdownStyle = createGlobalStyle`
  .upload-columns-select-dropdown.ant-select-dropdown .ant-select-item {
    font-size: 12px;
  }
`;

const ResultScreen = styled.div`
  display: flex;
  flex-direction: column;
  //align-items: center;
  //justify-content: center;
  min-height: 200px;
  padding-top: 25px;
  background: var(--color-surface-raised, #ffffff);

  .ant-switch-inner {
    background: var(--color-border, #dbeae3) !important;
  }

  .switch-checked > div::before {
    background: var(--color-primary, #41aaaf) !important;
  }
`;

const ResultHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ShowErrorsOnlyToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface UploadTransactionsColumnsModalProps {
  columnNames: string[];
  fileName: string;
  organisationId?: string;
}

export const UploadTransactionsColumnsModal: React.FC<
  UploadTransactionsColumnsModalProps
> = ({ columnNames, fileName, organisationId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const preferencesHook = usePreferences();
  const currentUser = useSelector(getAuthUser);
  const uploadTemp = useSelector(getUploadTransactionsTemp);
  const uploadLoading = useSelector(getOrgLoading);
  const uploadStatus = useSelector(getUploadExcelTransactionsStatus);
  const uploadValidationData = useSelector(
    getUploadExcelTransactionsValidationData
  );
  const uploadedFileTransactionsPage = useSelector(
    getUploadedFileTransactionsPage
  );
  const chartOfAccountsSelector = React.useMemo(
    () => getChartOfAccountsForOrganisation(organisationId),
    [organisationId]
  );
  const chartOfAccounts = useSelector(chartOfAccountsSelector);
  const accountNameByIdMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const row of chartOfAccounts) {
      if (row.id && row.name) map.set(row.id, row.name.trim());
    }
    return map;
  }, [chartOfAccounts]);
  const [activeTab, setActiveTab] = React.useState<string>('all');
  const hasFetchedInitialSuccessPage = React.useRef(false);
  const [showErrorsOnly, setShowErrorsOnly] = React.useState(true);
  const [validationTablePageSize, setValidationTablePageSize] =
    React.useState(10);

  // When we're on success view, load the first page of successful transactions (server-side paging)
  React.useEffect(() => {
    if (uploadStatus !== 'success') {
      hasFetchedInitialSuccessPage.current = false;
      return;
    }
    if (
      uploadValidationData?.importId &&
      organisationId &&
      !hasFetchedInitialSuccessPage.current
    ) {
      hasFetchedInitialSuccessPage.current = true;
      dispatch(
        fetchUploadedFileTransactionsRequest(
          organisationId,
          uploadValidationData.importId,
          1,
          10
        )
      );
    }
  }, [uploadStatus, uploadValidationData?.importId, organisationId, dispatch]);

  // Expected fields the project expects (labels); user maps each to an Excel column from the file.
  // Is Customer/Supplier (contact type) is excluded from the mapping UI.
  const expectedFields = React.useMemo(
    () =>
      excelColumnNames
        .filter((name) => isExcelProviderMappingField(name))
        .map((name, id) => ({ id, name })),
    []
  );

  // Mapping state: app field name -> selected Excel column name (from uploaded file)
  const [mappings, setMappings] = React.useState<
    Record<string, string | undefined>
  >({});

  // Auto-map on modal open: exact case-insensitive match, including synonyms (e.g. "Customer or Supplier" -> "Is Customer/Supplier")
  // Use FIELD_MATCH_ORDER so processing order doesn't change with UI display order
  React.useEffect(() => {
    const autoMappings: Record<string, string> = {};
    const usedExcelColumns = new Set<string>();
    const fieldsForMatch = FIELD_MATCH_ORDER.map(
      (name) => expectedFields.find((f) => f.name === name)!
    ).filter(Boolean);

    fieldsForMatch.forEach((field) => {
      const synonyms = EXPECTED_FIELD_SYNONYMS[field.name] ?? [field.name];
      const matchSet = new Set(synonyms.map((s) => s.toLowerCase().trim()));
      const matchedExcelColumn = columnNames.find((excelCol) => {
        const normalised = excelCol.toLowerCase().trim();
        return matchSet.has(normalised) && !usedExcelColumns.has(excelCol);
      });
      if (matchedExcelColumn) {
        autoMappings[field.name] = matchedExcelColumn;
        usedExcelColumns.add(matchedExcelColumn);
      }
    });

    setMappings(autoMappings);
  }, [columnNames, expectedFields]);

  const FUZZY_MIN_SCORE = 0.45;

  const handleTryFuzzyMapping = () => {
    setMappings((prev) => {
      const usedExcelColumns = new Set<string>(
        Object.values(prev).filter(
          (v): v is string => v !== undefined && v !== null
        )
      );
      const next = { ...prev };
      const fieldsForMatch = FIELD_MATCH_ORDER.map(
        (name) => expectedFields.find((f) => f.name === name)!
      ).filter(Boolean);

      fieldsForMatch.forEach((field) => {
        if (next[field.name] != null) return; // keep existing (exact) mappings
        const synonyms = EXPECTED_FIELD_SYNONYMS[field.name] ?? [];
        const containWords =
          field.name === 'Country'
            ? 'country'
            : field.name === 'VAT Number'
            ? ['vat number', 'tax number']
            : field.name === 'Transaction Date'
            ? 'date'
            : field.name === 'Reference'
            ? ['reference', 'ref']
            : field.name === 'Description'
            ? ['description', 'desc', 'details', 'notes', 'remarks', 'comment']
            : undefined;
        // Prevent "Invoice No", "Bill Number" etc. matching Transaction Date (they're reference/transaction number)
        const rejectIfContains =
          field.name === 'Transaction Date' ? [' no', ' number'] : undefined;
        const fuzzy = bestFuzzyMatch(
          field.name,
          columnNames,
          usedExcelColumns,
          FUZZY_MIN_SCORE,
          synonyms.filter((s) => s !== field.name),
          containWords,
          rejectIfContains
        );
        if (fuzzy?.candidate) {
          next[field.name] = fuzzy.candidate;
          usedExcelColumns.add(fuzzy.candidate);
        }
      });

      return next;
    });
  };

  const mappedColumns = React.useMemo(
    () => expectedFields.filter((f) => !!mappings[f.name]),
    [expectedFields, mappings]
  );

  const unmappedColumns = React.useMemo(
    () => expectedFields.filter((f) => !mappings[f.name]),
    [expectedFields, mappings]
  );

  const selectedExcelColumns = React.useMemo(
    () =>
      Object.values(mappings).filter(
        (value): value is string => value !== undefined && value !== null
      ),
    [mappings]
  );

  const handleChangeMapping = (appFieldName: string, value: string | null) => {
    setMappings((prev) => ({
      ...prev,
      [appFieldName]: value || undefined,
    }));
  };

  const getMapImportDisplayLabel = React.useCallback(
    (fieldName: string) => {
      const tooltipKey =
        LABEL_TO_TOOLTIP_KEY[fieldName] ?? TOOLTIP_TEXT_TO_KEY[fieldName];
      if (tooltipKey == null) {
        return fieldName;
      }
      return tryGetMapImportFieldLabel(tooltipKey, t) ?? fieldName;
    },
    [t]
  );

  const getOptionsForField = (appFieldName: string) => {
    const currentValue = mappings[appFieldName];
    return columnNames
      .filter(
        (excelCol) =>
          excelCol === currentValue || !selectedExcelColumns.includes(excelCol)
      )
      .map((name) => ({
        label: name,
        value: name,
      }));
  };

  const renderColumnsGrid = (fields: { id: number; name: string }[]) => {
    const itemsPerColumn = Math.ceil(fields.length / 3);
    const col1 = fields.slice(0, itemsPerColumn);
    const col2 = fields.slice(itemsPerColumn, itemsPerColumn * 2);
    const col3 = fields.slice(itemsPerColumn * 2);

    const renderColumnList = (fieldList: { id: number; name: string }[]) => (
      <ColumnList>
        {fieldList.map((field) => {
          const tooltipKey =
            LABEL_TO_TOOLTIP_KEY[field.name] ?? TOOLTIP_TEXT_TO_KEY[field.name];
          return (
            <ColumnItem key={field.id}>
              <ColumnName>
                <ColumnLabelWithTooltip>
                  {getMapImportDisplayLabel(field.name)}
                  {tooltipKey != null && (
                    <Tooltip
                      title={t(`mapImportFields.tooltips.${tooltipKey}`)}
                    >
                      <QuestionMarkIconWrapper>
                        <IconQuestionMark width={15} height={15} />
                      </QuestionMarkIconWrapper>
                    </Tooltip>
                  )}
                </ColumnLabelWithTooltip>
                {canEverBeOptionalMappingField(field.name) && (
                  <ColumnOptionalHint
                    $visible={isOptionalMappingField(field.name, mappings)}
                  >
                    (optional)
                  </ColumnOptionalHint>
                )}
              </ColumnName>
              <MapFormSelect
                placeholder={t('organisation.select-mapping-from-file')}
                width="60%"
                height="35px"
                bordercolor="var(--color-border, #dbeae3)"
                bgcolor="var(--color-surface-raised, #FFFFFF)"
                allowClear
                virtual={false}
                value={mappings[field.name]}
                onChange={(value) =>
                  handleChangeMapping(field.name, value as string | null)
                }
                options={getOptionsForField(field.name)}
                popupClassName="upload-columns-select-dropdown"
              />
            </ColumnItem>
          );
        })}
      </ColumnList>
    );

    return (
      <ColumnGrid>
        {renderColumnList(col1)}
        {renderColumnList(col2)}
        {renderColumnList(col3)}
      </ColumnGrid>
    );
  };

  const [previewColumns, setPreviewColumns] = React.useState<any[]>([]);
  const [previewData, setPreviewData] = React.useState<any[]>([]);

  React.useEffect(() => {
    const tempFile = uploadTemp?.file;
    if (!tempFile || !columnNames.length) {
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const XLSX = await import('xlsx');
        const data = e.target?.result;
        if (!data) {
          return;
        }

        let workbook: XLSXTypes.WorkBook;

        if (tempFile.name.endsWith('.csv')) {
          workbook = XLSX.read(data as string, { type: 'string' });
        } else {
          workbook = XLSX.read(data as string, { type: 'binary' });
        }

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        if (!worksheet) {
          return;
        }

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          raw: false,
        }) as any[][];

        if (!jsonData.length) {
          return;
        }

        // Try to find the row whose non-empty cells match our columnNames
        let headerIndex = jsonData.findIndex((row) => {
          if (!Array.isArray(row)) {
            return false;
          }
          const cells = row
            .filter((cell) => cell !== '' && cell != null)
            .map((cell) => String(cell));
          if (cells.length !== columnNames.length) {
            return false;
          }
          return cells.every((val, idx) => val === columnNames[idx]);
        });

        if (headerIndex === -1) {
          headerIndex = jsonData.findIndex(
            (row) =>
              Array.isArray(row) &&
              row.some((cell) => cell !== '' && cell != null)
          );
        }

        if (headerIndex === -1) {
          return;
        }

        const headerRow = jsonData[headerIndex] || [];

        // Only show as many preview columns as we have mapped titles for,
        // to avoid trailing empty Excel columns.
        const maxCols =
          columnNames.length > 0 ? columnNames.length : headerRow.length;
        const limitedHeaderRow = headerRow.slice(0, maxCols);

        const previewCols = limitedHeaderRow.map((cell, idx) => ({
          title: String(cell ?? ''),
          dataIndex: `col${idx}`,
          key: `col${idx}`,
        }));

        const dataRows = jsonData
          .slice(headerIndex + 1)
          .filter(
            (row) =>
              Array.isArray(row) &&
              row.some((cell) => cell !== '' && cell != null)
          )
          .slice(0, 3);

        const previewRows = dataRows.map((row, rowIdx) => {
          const record: any = { key: rowIdx };
          limitedHeaderRow.forEach((_, colIdx) => {
            record[`col${colIdx}`] = row[colIdx];
          });
          return record;
        });

        setPreviewColumns(previewCols);
        setPreviewData(previewRows);
      } catch {
        // swallow preview errors – mapping flow should still work
      }
    };

    reader.onerror = () => {
      // ignore preview errors
    };

    if (tempFile.name.endsWith('.csv')) {
      reader.readAsText(tempFile);
    } else {
      reader.readAsBinaryString(tempFile);
    }
  }, [uploadTemp, columnNames]);

  const getMappedFieldForExcelColumn = (
    excelColumnName: string
  ): string | null => {
    const entry = Object.entries(mappings).find(
      ([_, excelCol]) => excelCol === excelColumnName
    );
    return entry ? entry[0] : null;
  };

  const previewColumnsWithMappedTitles = React.useMemo(() => {
    return previewColumns.map((col: any) => {
      const excelHeader = col.title ?? '';
      const mappedField = getMappedFieldForExcelColumn(excelHeader);
      const mappedLabel =
        mappedField != null ? getMapImportDisplayLabel(mappedField) : null;
      return {
        ...col,
        title: (
          <PreviewColumnHeaderWrap>
            <span>{excelHeader}</span>
            <PreviewColumnHeaderMapped
              style={{ visibility: mappedLabel != null ? 'visible' : 'hidden' }}
            >
              {mappedLabel ?? '\u00A0'}
            </PreviewColumnHeaderMapped>
          </PreviewColumnHeaderWrap>
        ),
      };
    });
  }, [previewColumns, mappings, getMapImportDisplayLabel]);

  const SWITCH_TO_TRANSACTIONS_EVENT =
    'organisation-details-switch-to-transactions';

  const handleClose = () => {
    if (uploadStatus === 'success') {
      const importId = uploadValidationData?.importId;
      if (importId && currentUser?.userUuid) {
        preferencesHook(
          PreferenceActions.SET,
          PreferenceSections.TRANSACTIONS,
          {
            name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
            value: importId,
          },
          currentUser.userUuid
        );
      }
      // Do not auto-switch to Transactions tab; user can navigate manually
    }
    dispatch(clearUploadTransactionsTemp());
    dispatch(turnModalOff());
    // Refresh metadata list when closing after successful upload
    // (safety measure - saga also refreshes, but this ensures UI is updated)
    if (uploadStatus === 'success' && organisationId) {
      dispatch(fetchExcelFilesMetadataRequest(organisationId));
    }
  };

  const handleReset = () => {
    // Reset all mappings to empty object, which will clear all dropdown selects
    // since they are controlled components bound to mappings[column.id]
    setMappings({});
  };

  const handleContinue = () => {
    if (!organisationId) return;

    // mappings is already app field name -> Excel column name (API shape)
    const columnNameMapping: Record<string, string> = {};
    Object.entries(mappings).forEach(([appField, excelCol]) => {
      if (excelCol) columnNameMapping[appField] = excelCol;
    });

    dispatch(
      uploadExcelTransactionsRequest(
        organisationId,
        false, // forceDuplicates: do not force duplicates (reject by default)
        JSON.stringify(columnNameMapping)
      )
    );
  };

  // Check if all *required* expected fields are mapped to an Excel column.
  // Optional fields (including Account Name/Type when neither is mapped) may remain unmapped.
  const allColumnsMapped = React.useMemo(() => {
    if (!expectedFields.length) return false;

    const hasUnmappedRequiredField = unmappedColumns.some(
      (field) => !isOptionalMappingField(field.name, mappings)
    );

    return !hasUnmappedRequiredField;
  }, [expectedFields.length, unmappedColumns, mappings]);

  // After validation we're at step 3: Upload and Review completed, View Transactions active
  const isResultStep = uploadStatus === 'success' || uploadStatus === 'failure';

  // Result table: failure shows validationErrors (client-side paging); success shows uploadedFileTransactionsPage (server-side paging)
  const validationTableColumns = React.useMemo(() => {
    const hasFailureData =
      uploadStatus === 'failure' &&
      uploadValidationData?.validationErrors?.length;
    const hasSuccessData =
      uploadStatus === 'success' &&
      (uploadedFileTransactionsPage != null ||
        uploadValidationData?.importId != null);
    if (!hasFailureData && !hasSuccessData) {
      return [];
    }

    // Determine a representative set of rows to infer which fields are present.
    const sampleRows: ValidatedExcelTransaction[] =
      uploadStatus === 'failure'
        ? uploadValidationData?.validationErrors ?? []
        : uploadedFileTransactionsPage?.transactions ?? [];

    // Build columns from VALIDATION_COLUMN_KEY_MAP, based on which fields
    // actually appear in the backend data. This ensures we show columns
    // according to the backend payload (not just the mapping UI), while
    // still using the friendly display names from the map.
    // Columns that should always appear regardless of whether any row has data for them.
    const alwaysShowColumns = new Set(['accountCode', 'accountType']);

    const dataColumns = Object.entries(VALIDATION_COLUMN_KEY_MAP)
      // We handle "Error Reason" via the dedicated Status column, so skip it here.
      .filter(([displayName]) => displayName !== 'Error Reason')
      .filter(([displayName]) => isExcelProviderMappingField(displayName))
      .filter(
        ([_, dataKey]) =>
          alwaysShowColumns.has(dataKey) ||
          sampleRows.some(
            (row) =>
              row &&
              (row[dataKey as keyof ValidatedExcelTransaction] ?? null) !==
                null &&
              String(
                row[dataKey as keyof ValidatedExcelTransaction] ?? ''
              ).trim() !== ''
          )
      )
      .map(([displayName, dataKey]) => {
        return {
          title: displayName,
          dataIndex: dataKey,
          key: dataKey,
          width: 150,
          render: (val: unknown, record: Record<string, unknown>) => {
            const empty = val == null || String(val).trim() === '';
            if (dataKey === 'transactionDate' || dataKey === 'dueDate') {
              return formatExcelTransactionDateForDisplay(val);
            }
            if (dataKey === 'accountType') {
              if (empty) return '-';
              return getExcelTransactionAccountTypeLabel(String(val), t);
            }
            if (dataKey === 'accountCode') {
              const accountId =
                (record?._accountId as string) ?? (val as string | undefined);
              const accountName = record?._accountName as string | undefined;
              if (!accountId || String(accountId).trim() === '') {
                return accountName?.trim() || (empty ? '-' : val);
              }
              const id = String(accountId).trim();
              return (
                accountNameByIdMap.get(id) ??
                (accountName?.trim() || val || '-')
              );
            }
            return empty ? '-' : val;
          },
        };
      });
    const statusColumn = {
      title: t('main.status'),
      key: 'status',
      dataIndex: '_errorReason',
      width: 120,
      fixed: 'right' as const,
      render: (errorReason: string) => {
        if (uploadStatus === 'success') {
          return (
            <ColoredTag
              style={{ margin: 0 }}
              color="var(--color-status-success, #058181)"
              bgcolor="var(--color-status-success-bg, #E9F6F7)"
              bordered={true}
              bordercolor="var(--color-border, #dbeae3)"
              icon={<CheckOutlined />}
            >
              {t('main.valid')}
            </ColoredTag>
          );
        }
        const hasError =
          errorReason != null && String(errorReason).trim() !== '';
        if (hasError) {
          return (
            <Tooltip title={errorReason}>
              <span>
                <ColoredTag
                  style={{ margin: 0 }}
                  color="var(--color-status-danger, #D13131)"
                  bgcolor="var(--color-status-danger-bg, #FCE7E7)"
                  bordered={true}
                  icon={<CloseOutlined style={{ color: '#D13131' }} />}
                >
                  {t('common.error')}
                </ColoredTag>
              </span>
            </Tooltip>
          );
        }
        return (
          <ColoredTag
            style={{ margin: 0 }}
            color="var(--color-status-success, #058181)"
            bgcolor="var(--color-status-success-bg, #E9F6F7)"
            bordered={true}
            bordercolor="var(--color-border, #dbeae3)"
            icon={<CheckOutlined />}
          >
            {t('main.valid')}
          </ColoredTag>
        );
      },
    };

    const errorMessagesColumn = {
      title: t('organisation.error-messages'),
      key: 'errorMessages',
      dataIndex: '_errorReason',
      width: 320,
      fixed: 'right' as const,
      render: (errorReason: string | null | undefined) => {
        if (uploadStatus === 'success') return null;
        const messages = parseErrorMessages(errorReason);
        if (messages.length === 0) return null;
        return (
          <span
            style={{
              display: 'block',
              fontSize: 12,
              wordBreak: 'break-word',
            }}
          >
            {messages.map((msg, i) => (
              <span key={i} style={{ display: 'block' }}>
                {msg}
              </span>
            ))}
          </span>
        );
      },
    };

    return [
      ...dataColumns,
      statusColumn,
      ...(uploadStatus === 'failure' ? [errorMessagesColumn] : []),
    ];
  }, [
    uploadStatus,
    uploadValidationData,
    uploadedFileTransactionsPage,
    mappedColumns,
    t,
  ]);

  const validationTableData = React.useMemo(() => {
    if (uploadStatus === 'failure') {
      const rows = uploadValidationData?.validationErrors;
      if (!rows?.length) return [];
      return rows.map((row: ValidatedExcelTransaction, idx: number) => {
        const record: Record<string, unknown> = { key: idx };
        validationTableColumns.forEach((col) => {
          const dataKey = col.dataIndex as keyof ValidatedExcelTransaction;
          if (dataKey && dataKey in row) {
            record[dataKey as string] = row[dataKey];
          }
        });
        record._errorReason = row.errorReason;
        record._accountId = row.accountId;
        record._accountName = row.accountName;
        return record;
      });
    }
    if (uploadStatus === 'success') {
      const rows = uploadedFileTransactionsPage?.transactions;
      if (!rows?.length) return [];
      return rows.map((row: ValidatedExcelTransaction, idx: number) => {
        const record: Record<string, unknown> = { key: idx };
        validationTableColumns.forEach((col) => {
          const dataKey = col.dataIndex as keyof ValidatedExcelTransaction;
          if (dataKey && dataKey in row) {
            record[dataKey as string] = row[dataKey];
          }
        });
        record._errorReason = row.errorReason ?? '';
        record._accountId = row.accountId;
        record._accountName = row.accountName;
        return record;
      });
    }
    return [];
  }, [
    uploadStatus,
    uploadValidationData?.validationErrors,
    uploadedFileTransactionsPage?.transactions,
    validationTableColumns,
  ]);

  const filteredValidationTableData = React.useMemo(() => {
    if (uploadStatus !== 'failure' || !showErrorsOnly) {
      return validationTableData;
    }
    return validationTableData.filter(
      (record) =>
        record._errorReason != null && String(record._errorReason).trim() !== ''
    );
  }, [validationTableData, uploadStatus, showErrorsOnly]);

  const handleExportTransactions = React.useCallback(async () => {
    const XLSX = await import('xlsx');
    if (!filteredValidationTableData.length || !validationTableColumns.length)
      return;
    const headers = validationTableColumns.map((col) => col.title as string);
    const exportRows = filteredValidationTableData.map(
      (record: Record<string, unknown>) => {
        const row: Record<string, string | number | null> = {};
        validationTableColumns.forEach((col) => {
          const key = col.title as string;
          const dataIndex = col.dataIndex as string;
          const val = record[dataIndex];
          if (key === 'Status') {
            const hasError =
              record._errorReason != null &&
              String(record._errorReason).trim() !== '';
            row[key] =
              uploadStatus === 'success' || !hasError
                ? t('main.valid')
                : t('common.error');
          } else if (key === 'Error Messages') {
            const messages = parseErrorMessages(
              record._errorReason as string | null | undefined
            );
            row[key] = messages.length ? messages.join('\n') : '';
          } else if (
            (dataIndex === 'transactionDate' || dataIndex === 'dueDate') &&
            val != null
          ) {
            row[key] = formatExcelTransactionDateForDisplay(val);
          } else {
            row[key] = val != null && val !== '' ? String(val) : '';
          }
        });
        return row;
      }
    );
    const ws = XLSX.utils.json_to_sheet(exportRows, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      uploadStatus === 'failure' ? 'Validation errors' : 'Transactions'
    );
    const filename =
      uploadStatus === 'failure'
        ? 'validation-errors.xlsx'
        : 'transactions.xlsx';
    XLSX.writeFile(wb, filename);
  }, [filteredValidationTableData, validationTableColumns, uploadStatus, t]);

  return (
    <ModalWrapper>
      <SuccessHeader>
        <HeaderLeft>
          <CheckIconWrapper>
            <GoldCheck style={{ width: '32px', height: '32px' }} />
          </CheckIconWrapper>
          <HeaderText>
            <SuccessMessage>
              Your file has been uploaded successfully.
            </SuccessMessage>
            <FileName>{fileName}</FileName>
          </HeaderText>
        </HeaderLeft>
        <HeaderRight>
          <ProgressTracker>
            <ProgressStep $isCompleted>
              <StepCircle $isCompleted>
                <UploadCheck />
              </StepCircle>
              <span>{t('common.upload')}</span>
            </ProgressStep>
            <StepConnector />
            <ProgressStep $isCompleted={isResultStep} $isActive={!isResultStep}>
              <StepCircle $isCompleted={isResultStep} $isActive={!isResultStep}>
                {isResultStep ? <UploadCheck /> : '2'}
              </StepCircle>
              <span>{t('common.review')}</span>
            </ProgressStep>
            <StepConnector />
            <ProgressStep $isActive={isResultStep}>
              <StepCircle $isActive={isResultStep}>3</StepCircle>
              <span>{t('organisation.view-transactions')}</span>
            </ProgressStep>
          </ProgressTracker>
        </HeaderRight>
      </SuccessHeader>

      <Spin spinning={uploadLoading} size="large">
        <ModalContent>
          {uploadStatus === 'success' || uploadStatus === 'failure' ? (
            <>
              <ResultScreen
                data-validation-data={
                  (uploadStatus === 'failure' || uploadStatus === 'success') &&
                  uploadValidationData
                    ? JSON.stringify(uploadValidationData)
                    : undefined
                }
              >
                {validationTableColumns.length > 0 &&
                  (validationTableData.length > 0 ||
                    (uploadStatus === 'success' &&
                      uploadValidationData?.importId)) && (
                    <>
                      {uploadStatus === 'failure' ? (
                        <ResultStepWarningBlock>
                          <ExclamationCircleOutlined />
                          <ResultStepTitleBlock
                            style={{
                              marginTop: 0,
                              marginBottom: 0,
                              paddingBottom: 0,
                              borderBottom: 'none',
                            }}
                          >
                            <ResultStepTitle>
                              {t('organisation.validation-errors')}
                            </ResultStepTitle>
                            <ResultStepDescription>
                              {t(
                                'organisation.upload-validation-errors-description'
                              )}
                            </ResultStepDescription>
                          </ResultStepTitleBlock>
                        </ResultStepWarningBlock>
                      ) : (
                        <ResultStepSuccessBlock>
                          <CheckCircleOutlined />
                          <ResultStepTitleBlock
                            style={{
                              marginTop: 0,
                              marginBottom: 0,
                              paddingBottom: 0,
                              borderBottom: 'none',
                            }}
                          >
                            <ResultStepTitle>
                              {t(
                                'organisation.upload-successful-transactions-title'
                              )}
                            </ResultStepTitle>
                            <ResultStepDescription>
                              {t(
                                'organisation.upload-successful-transactions-description'
                              )}
                              <br />
                              <br />
                              {t(
                                'organisation.upload-source-types-auto-updated'
                              )}{' '}
                              <ResultStepHelpLink
                                href={UPLOAD_TRANSACTIONS_HELP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t('organisation.read-more')}
                              </ResultStepHelpLink>
                            </ResultStepDescription>
                          </ResultStepTitleBlock>
                        </ResultStepSuccessBlock>
                      )}
                      <ResultHeaderRow>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {uploadStatus === 'failure' && (
                            <ShowErrorsOnlyToggle>
                              <span style={{ fontSize: 12 }}>
                                {t('organisation.show-errors-only')}
                              </span>
                              <Tooltip
                                title={
                                  showErrorsOnly
                                    ? t('common.toggle-on-click-to-turn-off')
                                    : t('common.toggle-off-click-to-turn-on')
                                }
                              >
                                <Switch
                                  size="small"
                                  checked={showErrorsOnly}
                                  className={
                                    showErrorsOnly ? 'switch-checked' : ''
                                  }
                                  aria-label={t(
                                    'organisation.show-errors-only'
                                  )}
                                  onChange={(checked) =>
                                    setShowErrorsOnly(checked)
                                  }
                                />
                              </Tooltip>
                            </ShowErrorsOnlyToggle>
                          )}
                        </div>
                        <ExportButton
                          icon={
                            <IconExport style={{ width: 20, height: 20 }} />
                          }
                          onClick={handleExportTransactions}
                        >
                          <span>{t('organisation.export-transactions-label')}</span>
                        </ExportButton>
                      </ResultHeaderRow>
                      <PreviewTableWrapper
                        style={{
                          //height: 'calc(100vh - 320px)',
                          overflowY: 'auto',
                        }}
                      >
                        <ScopeTable
                          className="validation-errors-table"
                          tableDataSource={filteredValidationTableData}
                          originalDataSource={filteredValidationTableData}
                          columns={validationTableColumns}
                          pagination={true}
                          rowsPerPage={validationTablePageSize}
                          enableColumnsCustomization={false}
                          rowHeight={32}
                          showSizeChanger={true}
                          getNewPageSize={(pageSize) =>
                            setValidationTablePageSize(pageSize)
                          }
                          transactionType=""
                          reportId=""
                          total={
                            uploadStatus === 'success'
                              ? uploadedFileTransactionsPage?.total
                              : undefined
                          }
                          customPagination={uploadStatus === 'success'}
                          onPageChange={
                            uploadStatus === 'success' &&
                            organisationId &&
                            uploadValidationData?.importId
                              ? (page, pageSize) => {
                                  setValidationTablePageSize(pageSize);
                                  dispatch(
                                    fetchUploadedFileTransactionsRequest(
                                      organisationId,
                                      uploadValidationData.importId!,
                                      page,
                                      pageSize
                                    )
                                  );
                                }
                              : undefined
                          }
                          scroll={{ x: 'max-content' }}
                        />
                      </PreviewTableWrapper>
                    </>
                  )}
              </ResultScreen>
              <FooterButtons>
                {uploadStatus === 'success' ? (
                  <>
                    <span />
                    <FooterRightButtons>
                      <FooterButton type="dark-orange" onClick={handleClose}>
                        Finish
                      </FooterButton>
                    </FooterRightButtons>
                  </>
                ) : (
                  <>
                    <StyledPopconfirm
                      title={t('common.cancel')}
                      description={t(
                        'organisation.close-window-fix-mapping-description'
                      )}
                      onConfirm={handleClose}
                      okText={t('organisation.yes-close')}
                      cancelText={t('common.no')}
                      placement="top"
                      overlayStyle={{ width: '400px', maxWidth: '400px' }}
                    >
                      <span>
                        <FooterButton type="default">
                          {t('common.cancel')}
                        </FooterButton>
                      </span>
                    </StyledPopconfirm>
                    <FooterRightButtons>
                      <FooterButton
                        type="default"
                        onClick={() => dispatch(clearUploadExcelResult())}
                      >
                        {t('common.back')}
                      </FooterButton>
                      <FooterButton
                        type="dark-orange"
                        onClick={handleContinue}
                        disabled={true}
                      >
                        Continue
                      </FooterButton>
                    </FooterRightButtons>
                  </>
                )}
              </FooterButtons>
            </>
          ) : (
            <>
              <UploadColumnsDropdownStyle />
              <ContentHeader>
                <ContentHeaderLeft>
                  <ContentTitle>{t('organisation.map-import-fields-title')}</ContentTitle>
                </ContentHeaderLeft>
                <TabsContainer>
                  <ScopeTabs
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key)}
                    centered
                    items={[
                      { key: 'all', label: t('common.all') },
                      {
                        key: 'mapped',
                        label: `Mapped (${mappedColumns.length})`,
                      },
                      {
                        key: 'unmapped',
                        label: `Unmapped (${unmappedColumns.length})`,
                      },
                    ]}
                  />
                </TabsContainer>
                <ContentHeaderRight>
                  <Tooltip
                    placement="left"
                    title={t('organisation.map-columns-ai-tooltip')}
                  >
                    <span style={{ display: 'inline-block' }}>
                      <FuzzyMappingButton
                        type="dark-orange"
                        onClick={handleTryFuzzyMapping}
                        disabled={allColumnsMapped}
                      >
                        <FuzzyMappingIcon />
                        Try mapping with AI
                      </FuzzyMappingButton>
                    </span>
                  </Tooltip>
                </ContentHeaderRight>
              </ContentHeader>

              <TabsContent>
                {activeTab === 'all' && renderColumnsGrid(expectedFields)}
                {activeTab === 'mapped' && renderColumnsGrid(mappedColumns)}
                {activeTab === 'unmapped' &&
                  (unmappedColumns.length === 0 ? (
                    <AllMappedMessage>
                      All columns have been successfully mapped
                    </AllMappedMessage>
                  ) : (
                    renderColumnsGrid(unmappedColumns)
                  ))}
              </TabsContent>

              <PreviewTableWrapper>
                {previewColumns.length > 0 && previewData.length > 0 && (
                  <ScopeTable
                    className="preview-table"
                    tableDataSource={previewData}
                    originalDataSource={previewData}
                    columns={previewColumnsWithMappedTitles}
                    pagination={false}
                    rowsPerPage={3}
                    enableColumnsCustomization={false}
                    rowHeight={32}
                    showSizeChanger={false}
                    getNewPageSize={() => {}}
                    transactionType=""
                    reportId=""
                    scroll={{ x: 'max-content' }}
                    titleComponent={
                      <PreviewTableTitle>
                        Preview sample data from uploaded file
                      </PreviewTableTitle>
                    }
                  />
                )}
              </PreviewTableWrapper>

              <FooterButtons>
                <StyledPopconfirm
                      title={t('organisation.cancel-import')}
                      description={t(
                        'organisation.cancel-import-description'
                      )}
                  onConfirm={handleClose}
                      okText={t('common.cancel')}
                      cancelText={t('common.no')}
                  placement="top"
                  overlayStyle={{ width: '400px', maxWidth: '400px' }}
                >
                  <span>
                    <FooterButton type="default">
                      {t('common.cancel')}
                    </FooterButton>
                  </span>
                </StyledPopconfirm>
                <FooterRightButtons>
                  <StyledPopconfirm
                    title={t('organisation.reset-mapping')}
                    description={t('organisation.reset-mapping-description')}
                    onConfirm={handleReset}
                    okText={t('common.reset')}
                    cancelText={t('common.cancel')}
                    placement="top"
                    overlayStyle={{ width: '400px', maxWidth: '400px' }}
                  >
                    <span>
                      <FooterButton type="default">{t('common.reset')}</FooterButton>
                    </span>
                  </StyledPopconfirm>
                  <FooterButton
                    type="dark-orange"
                    onClick={handleContinue}
                    disabled={
                      !allColumnsMapped || !organisationId || uploadLoading
                    }
                  >
                    Continue
                  </FooterButton>
                </FooterRightButtons>
              </FooterButtons>
            </>
          )}
        </ModalContent>
      </Spin>
    </ModalWrapper>
  );
};
